import { useEffect, useRef, useState, useCallback } from "react";
import { Download, Share, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

type InstallEventType = "shown" | "accepted" | "dismissed" | "failed";

const trackEvent = (
  event_type: InstallEventType,
  platform: "ios" | "android" | "other",
  details?: Record<string, unknown>,
) => {
  try {
    void supabase.from("install_prompt_events").insert({
      event_type,
      platform,
      user_agent: navigator.userAgent,
      details: (details ?? null) as never,
    });
  } catch {
    // analytics must never throw
  }
};

const STORAGE_KEY = "a2hs-dismissed-at";
const DISMISS_DAYS = 14;
const SHOW_DELAY_MS = 8000;
const APP_ICON =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/0TPLCMws1oRoWBGPW4SBZFE3dEp1/uploads/1762890537261-ChatGPT%20Image%20Sep%2030%2C%202025%2C%2011_31_11%20PM.png";

interface BIPEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const isMobile = () =>
  /android|iphone|ipad|ipod/i.test(navigator.userAgent.toLowerCase());

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent.toLowerCase());

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  // @ts-expect-error iOS Safari
  window.navigator.standalone === true;

const isDismissed = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

export default function InstallPrompt() {
  const [open, setOpen] = useState(false);
  const [showFab, setShowFab] = useState(false);
  const [deferred, setDeferred] = useState<BIPEvent | null>(null);
  const [iosMode, setIosMode] = useState(false);
  const shownRef = useRef(false);
  const platformRef = useRef<"ios" | "android" | "other">("other");

  const trackShown = useCallback(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    trackEvent("shown", platformRef.current, { trigger: "auto" });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isMobile() || isStandalone()) return;

    const ios = isIOS();
    setIosMode(ios);
    platformRef.current = ios
      ? "ios"
      : /android/i.test(navigator.userAgent)
        ? "android"
        : "other";

    const onBIP = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BIPEvent);
    };
    const onInstalled = () => {
      setOpen(false);
      setShowFab(false);
      trackEvent("accepted", platformRef.current, { source: "appinstalled" });
      try {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
      } catch {}
    };

    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);

    const dismissed = isDismissed();

    const t = window.setTimeout(() => {
      if (isStandalone()) return;
      if (!dismissed) {
        setOpen(true);
        trackShown();
      }
      setShowFab(true);
    }, SHOW_DELAY_MS);

    return () => {
      window.clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [trackShown]);

  const dismiss = useCallback(() => {
    setOpen(false);
    trackEvent("dismissed", platformRef.current);
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {}
  }, []);

  const openFromFab = useCallback(() => {
    setOpen(true);
    if (!shownRef.current) {
      shownRef.current = true;
      trackEvent("shown", platformRef.current, { trigger: "fab" });
    }
  }, []);

  const handleAdd = useCallback(async () => {
    if (deferred) {
      try {
        await deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === "accepted") {
          trackEvent("accepted", platformRef.current, { source: "prompt" });
          setOpen(false);
          setShowFab(false);
        } else {
          trackEvent("dismissed", platformRef.current, { source: "prompt" });
        }
        setDeferred(null);
      } catch (err) {
        trackEvent("failed", platformRef.current, {
          message: err instanceof Error ? err.message : String(err),
        });
      }
    } else if (iosMode) {
      // iOS — instructions visible; nothing to call
    } else {
      trackEvent("failed", platformRef.current, { reason: "no-deferred-prompt" });
    }
  }, [deferred, iosMode]);

  if (typeof window === "undefined") return null;
  if (!isMobile() || isStandalone()) return null;

  return (
    <>
      {/* Floating fallback */}
      {showFab && !open && (
        <button
          aria-label="Install app"
          onClick={openFromFab}
          className={cn(
            "fixed bottom-20 right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full",
            "bg-primary text-primary-foreground shadow-lg",
            "animate-fade-in hover-scale",
          )}
        >
          <Download className="h-5 w-5" />
        </button>
      )}

      {/* Bottom sheet */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Install Our App"
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
        >
          <div
            className="absolute inset-0 bg-black/50 animate-fade-in"
            onClick={dismiss}
          />
          <div
            className={cn(
              "relative w-full max-w-md rounded-t-2xl sm:rounded-2xl border border-border bg-background p-5 shadow-2xl",
              "animate-fade-in",
            )}
            style={{ animation: "fade-in 0.3s ease-out, scale-in 0.2s ease-out" }}
          >
            <button
              onClick={dismiss}
              aria-label="Close"
              className="absolute right-3 top-3 rounded-full p-1 text-muted-foreground hover:bg-accent"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-3">
              <img
                src={APP_ICON}
                alt="App icon"
                className="h-14 w-14 rounded-2xl object-cover shadow-md"
                loading="lazy"
              />
              <div className="min-w-0">
                <h2 className="text-lg font-semibold leading-tight">
                  Install Our App
                </h2>
                <p className="text-sm text-muted-foreground">
                  Get faster access, better experience, and instant learning on your home screen.
                </p>
              </div>
            </div>

            {iosMode ? (
              <div className="mt-4 rounded-xl bg-muted/60 p-3 text-sm">
                <p className="flex items-center gap-2">
                  Tap the
                  <Share className="inline h-4 w-4 text-primary" />
                  Share icon, then choose
                  <span className="inline-flex items-center gap-1 rounded-md bg-background px-2 py-0.5 font-medium border border-border">
                    <Plus className="h-3 w-3" /> Add to Home Screen
                  </span>
                </p>
              </div>
            ) : null}

            <div className="mt-5 flex gap-2">
              <Button variant="ghost" className="flex-1" onClick={dismiss}>
                Maybe Later
              </Button>
              <Button
                className="flex-1"
                onClick={handleAdd}
                disabled={iosMode && !deferred}
              >
                <Download className="h-4 w-4" />
                Add Now
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
